import React, { useEffect, useState } from "react";

import {
  ConsentManagementConfigVersion,
  ConsentModel,
  invalidateQuery,
} from "@/api";
import { createConsentManagementConfig } from "@/consent-management/api/createConsentManagementConfig";
import { updateConsentManagementConfigVersion } from "@/consent-management/api/updateConsentManagementConfigVersion";
import { ConsentManagerConfigSchema } from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/schema";
import { consentManagementIntegrations } from "@/consent-management/consentManagement";
import { buildCreateConsentConfigRequest } from "@/consent-management/utils/initialConsentConfig";
import { Banner } from "@/design-system/components/Banner/Banner";
import { Checkbox } from "@/design-system/components/Checkbox/Checkbox";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Typography } from "@/design-system/components/Typography/Typography";
import { useCreateZendeskSupportLink } from "@/destinations/v2/CatalogPage/zendeskSupportLink";
import { unpackRequestErrorMessage } from "@/utils/request";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { Choice, ChoiceCards } from "../ChoiceCards";
import { ConsentLoadError } from "../ConsentLoadError";
import { ESSENTIAL_ALWAYS_SENT, MANAGED_REGIONS_OPT_IN } from "../copy";
import {
  applyTargetingToDraft,
  changePreset,
  ConsentTargeting,
  countInactiveRules,
  countOtherTargetedConfigs,
  draftFingerprint,
  GeotargetingBehavior,
  newTargetId,
  TargetingPreset,
  targetingChangesDraft,
} from "../targeting";
import { targetingToSave, useCurrentTargeting } from "../useCurrentTargeting";
import { ConsentManagerStepProps } from "../types";
import {
  freshpaintConsent,
  NO_WRITE_ACCESS_REASON,
  useFreshpaintConsent,
} from "../useFreshpaintConsent";

const consentModels: Choice<ConsentModel>[] = [
  {
    value: "opt-in",
    title: "Opt-in",
    description:
      "Events reach a destination only after a visitor consents to its category. It’s the stricter choice, and the one EU and UK privacy laws generally expect.",
  },
  {
    value: "opt-out",
    title: "Opt-out",
    description:
      "Events reach destinations right away, and stop for a category once a visitor opts out of it.",
  },
];

const everywhereDescriptions: Record<GeotargetingBehavior, string> = {
  managed:
    "One banner for every visitor. Freshpaint’s built-in regional rules ask for opt-in in the EU, UK and US privacy states.",
  custom:
    "One default banner. Your rules on the Consent Manager page still apply, and Freshpaint’s built-in regional opt-in stays off.",
  disabled:
    "One banner for every visitor, with the same consent model in every region.",
};

const otherPresets: Choice<TargetingPreset>[] = [
  {
    value: "regions",
    title: "Different rules by region",
    description:
      "For example, opt-in in the EU, UK and US privacy states and opt-out in the rest of the US.",
  },
  {
    value: "sites",
    title: "Different rules for each site",
    description: "Give each domain its own consent model and privacy policy.",
  },
  {
    value: "sites-and-regions",
    title: "Both sites and regions",
    description:
      "Rules for each site and region. Where a site and a region disagree, opt-in wins.",
  },
];

function everywhereBehavior(
  draft: ConsentManagementConfigVersion | undefined,
  targeting: ConsentTargeting,
): GeotargetingBehavior {
  if (!draft) {
    return "managed";
  }
  return (
    applyTargetingToDraft(draft, { ...targeting, preset: "everywhere" })
      .freshpaintConfig?.geotargetingBehavior ?? "disabled"
  );
}

const targetNoun: Record<TargetingPreset, string> = {
  everywhere: "visitor",
  regions: "region",
  sites: "site",
  "sites-and-regions": "site and region",
};

export function SetUpStep({
  data,
  context,
  updateData,
  goNext,
  complete,
}: ConsentManagerStepProps) {
  const consent = useFreshpaintConsent();
  const { draft, known } = context;
  const createSupportLink = useCreateZendeskSupportLink();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const managedBy = consent.otherLiveConfig
    ? consentManagementIntegrations[consent.otherLiveConfig.type].name
    : null;
  const { targeting, storeTargeting } = useCurrentTargeting(data, context);
  const otherTargetedConfigs = countOtherTargetedConfigs(draft);
  const inactiveRules = countInactiveRules(draft);
  const behaviorWithoutBuiltIn = everywhereBehavior(draft, {
    ...targeting,
    useBuiltInRegions: false,
  });
  const targetingPresets: Choice<TargetingPreset>[] = [
    {
      value: "everywhere",
      title: "The same rules everywhere",
      description: everywhereDescriptions[everywhereBehavior(draft, targeting)],
    },
    ...otherPresets,
  ];

  useEffect(() => {
    if (!consent.isLoading && managedBy !== data.managedBy) {
      updateData({ managedBy });
    }
  }, [consent.isLoading, managedBy, data.managedBy, updateData]);

  function choosePreset(preset: TargetingPreset) {
    updateData(storeTargeting(changePreset(targeting, preset, newTargetId)));
  }

  async function run(action: () => Promise<void>) {
    setSaving(true);
    setError(null);
    try {
      await action();
      goNext();
    } catch (actionError) {
      setError(unpackRequestErrorMessage(actionError));
      setSaving(false);
    }
  }

  const create = () =>
    run(async () => {
      await createConsentManagementConfig(
        buildCreateConsentConfigRequest({
          integration: freshpaintConsent,
          consentModel:
            targeting.preset === "everywhere"
              ? data.consentModel
              : targeting.defaultConsentModel,
          enabledCategorizableDestinations: consent.destinations,
        }),
      );
      await invalidateQuery(["get_consent_management_configs", {}]);
      // Targeting picked before the banner existed was stored without a
      // draft. Recording the new draft keeps it current.
      const created = await consent.fetchLatestDraft();
      updateData({ targetingDraft: draftFingerprint(created) });
    });

  // Targeted presets save on the next step. Going back to one banner has no
  // step of its own, so it saves here.
  const continueWithExisting = () =>
    run(async () => {
      if (targeting.preset !== "everywhere") {
        return;
      }
      const latest = await consent.fetchLatestDraft();
      const edits = latest && targetingToSave(data, draft, latest);
      if (latest && edits && targetingChangesDraft(latest, edits)) {
        const next = applyTargetingToDraft(latest, edits);
        await updateConsentManagementConfigVersion({
          type: freshpaintConsent.slug,
          freshpaintConfig: next.freshpaintConfig,
        } as Partial<ConsentManagerConfigSchema> &
          Pick<ConsentManagerConfigSchema, "type">);
      }
    });

  const presetPicker = (
    <div className="flex flex-col gap-2">
      <Typography variant="label-md">Where does your banner run?</Typography>
      <ChoiceCards
        label="Where your banner runs"
        choices={targetingPresets}
        value={targeting.preset}
        onChange={choosePreset}
      />
      {otherTargetedConfigs > 0 &&
        !(targeting.preset === "everywhere" && targeting.useBuiltInRegions) && (
          <Typography variant="body-sm" color="subtle">
            The {otherTargetedConfigs} targeted{" "}
            {otherTargetedConfigs === 1 ? "rule" : "rules"} already set up on
            the Consent Manager page stay as they are.
          </Typography>
        )}
      {targeting.preset === "everywhere" &&
        consent.config &&
        behaviorWithoutBuiltIn !== "managed" && (
          <label className="flex cursor-pointer items-start gap-2">
            <Checkbox
              checked={targeting.useBuiltInRegions === true}
              onCheckedChange={(checked) =>
                updateData(
                  storeTargeting({
                    ...targeting,
                    useBuiltInRegions: checked === true,
                  }),
                )
              }
            />
            <Typography variant="body-sm">
              Ask for opt-in in the EU, UK and US privacy states with
              Freshpaint’s built-in regional rules
              {behaviorWithoutBuiltIn === "custom" &&
                `. This turns off the ${otherTargetedConfigs} ${otherTargetedConfigs === 1 ? "rule" : "rules"} on the Consent Manager page.`}
            </Typography>
          </label>
        )}
      {targeting.preset !== "everywhere" && inactiveRules > 0 && (
        <Typography variant="body-sm" color="subtle">
          {inactiveRules} inactive {inactiveRules === 1 ? "rule" : "rules"} on
          the Consent Manager page will turn on with targeted rules. You can
          remove {inactiveRules === 1 ? "it" : "them"} on the next step.
        </Typography>
      )}
    </div>
  );

  if (consent.isLoading) {
    return (
      <WizardStepLayout
        title="Set up your consent banner"
        primaryAction={{ label: "Continue", onClick: goNext, disabled: true }}
      >
        <LoadingSpinner className="size-6" />
      </WizardStepLayout>
    );
  }

  if (consent.loadError) {
    return (
      <WizardStepLayout
        title="Set up your consent banner"
        primaryAction={{ label: "Try again", onClick: consent.retry }}
      >
        <ConsentLoadError retry={consent.retry} />
      </WizardStepLayout>
    );
  }

  if (consent.otherLiveConfig) {
    const vendor = consentManagementIntegrations[consent.otherLiveConfig.type];
    return (
      <WizardStepLayout
        title="Consent is already managed here"
        description={`${vendor.name} is published and on in this environment, so Freshpaint already follows your visitors’ consent choices.`}
        primaryAction={{
          label: "Finish",
          onClick: complete,
        }}
      />
    );
  }

  if (!consent.hasEntitlement) {
    return (
      <WizardStepLayout
        title="Set up your consent banner"
        primaryAction={{
          label: "Continue",
          onClick: goNext,
          disabled: true,
          disabledReason: "Your plan doesn’t include the Consent Manager",
        }}
      >
        <Banner type="info">
          Your plan doesn’t include the Freshpaint Consent Manager.{" "}
          <a
            href={createSupportLink(freshpaintConsent.entitlementKey)}
            target="_blank"
            rel="noopener noreferrer"
            className="cursor-pointer underline"
          >
            Contact us
          </a>{" "}
          to add it.
        </Banner>
      </WizardStepLayout>
    );
  }

  if (consent.config) {
    const model = consentModels.find(
      (option) => option.value === draft?.consentModel,
    );
    return (
      <WizardStepLayout
        title="Your consent banner is set up"
        description="Next, check how it treats each destination and what visitors see."
        primaryAction={{
          label: "Continue",
          onClick: continueWithExisting,
          loading: saving,
          disabled: !known || consent.canWrite === false,
          disabledReason: NO_WRITE_ACCESS_REASON,
        }}
      >
        {model && (
          <Typography variant="body-md">
            {targeting.preset === "everywhere" ? "It" : "By default, it"} uses
            the <strong>{model.title.toLowerCase()}</strong> model.{" "}
            {model.description}
            {model.value === "opt-out" &&
              targeting.preset === "everywhere" &&
              everywhereBehavior(draft, targeting) === "managed" && (
                <> {MANAGED_REGIONS_OPT_IN}</>
              )}{" "}
            {ESSENTIAL_ALWAYS_SENT}
          </Typography>
        )}
        {presetPicker}
        {error && <Banner type="error">{error}</Banner>}
      </WizardStepLayout>
    );
  }

  return (
    <WizardStepLayout
      title="Set up your consent banner"
      description={`The Freshpaint Consent Manager shows visitors a banner and sends their events only to the destinations their consent choices allow. ${ESSENTIAL_ALWAYS_SENT} Nothing changes on your site until you publish and turn it on.`}
      primaryAction={{
        label: "Create consent banner",
        onClick: create,
        loading: saving,
        disabled: consent.canWrite === false,
        disabledReason: NO_WRITE_ACCESS_REASON,
      }}
    >
      {presetPicker}
      {targeting.preset === "everywhere" ? (
        <div className="flex flex-col gap-2">
          <Typography variant="label-md">How do visitors consent?</Typography>
          <ChoiceCards
            label="Consent model"
            choices={consentModels}
            value={data.consentModel}
            onChange={(consentModel) => updateData({ consentModel })}
          />
          {data.consentModel === "opt-out" && (
            <Typography variant="body-sm" color="subtle">
              {MANAGED_REGIONS_OPT_IN}
            </Typography>
          )}
        </div>
      ) : (
        <Typography variant="body-sm" color="subtle">
          Next, you’ll choose opt-in or opt-out for each{" "}
          {targetNoun[targeting.preset]}. Your rules replace Freshpaint’s
          built-in opt-in for the EU, UK and US privacy states. Destination
          categories are shared across every rule, so you only set them once.
        </Typography>
      )}
      {error && <Banner type="error">{error}</Banner>}
    </WizardStepLayout>
  );
}
